import React, { useState } from 'react';

const Travel: React.FC = () => {
  const [origin, setOrigin] = useState('BOS');
  const [departDate, setDepartDate] = useState('2027-06-23');
  const [returnDate, setReturnDate] = useState('2027-06-28');
  const [passengers, setPassengers] = useState(2);
  const [originError, setOriginError] = useState(false);

  const handleFlightSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const code = origin.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) {
      setOriginError(true);
      return;
    }
    setOriginError(false);
    const passengerSegment = passengers > 1 ? `/${passengers}adults` : '';
    const url = `https://www.kayak.com/flights/${code}-PUJ/${departDate}/${returnDate}${passengerSegment}?sort=bestflight_a`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="travel" className="py-12 md:py-20 bg-wedding-cream">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-center text-wedding-navy mb-8 md:mb-16">
          Travel & Accommodations
        </h2>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-lg mb-6 md:mb-8">
            <h3 className="font-serif text-2xl sm:text-3xl text-wedding-gold mb-4 md:mb-6 text-center">
              Getting to Punta Cana
            </h3>
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-wedding-gold mt-1 mr-2 sm:mr-3 flex-shrink-0"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                </svg>
                <div>
                  <p className="font-semibold text-sm sm:text-base">Airport:</p>
                  <p className="text-gray-700 text-sm sm:text-base">
                    Punta Cana International Airport (PUJ)
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-wedding-gold mt-1 mr-2 sm:mr-3 flex-shrink-0"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div>
                  <p className="text-gray-700 text-sm sm:text-base">
                    Most major airlines offer direct flights to Punta Cana from major US cities.
                    We recommend arriving a day early to settle in and enjoy the resort!
                  </p>
                </div>
              </div>
              <form
                onSubmit={handleFlightSearch}
                className="bg-wedding-cream p-4 sm:p-6 rounded-lg mt-2"
              >
                <p className="font-semibold text-base sm:text-lg mb-4 text-center text-wedding-navy">
                  Search Flights to Punta Cana
                </p>
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Departure Airport Code
                    </label>
                    <input
                      type="text"
                      value={origin}
                      onChange={(e) => {
                        setOrigin(e.target.value.toUpperCase());
                        setOriginError(false);
                      }}
                      placeholder="e.g. BOS"
                      maxLength={3}
                      required
                      className={`w-full px-3 py-2 rounded-md border text-sm sm:text-base uppercase focus:outline-none focus:ring-2 focus:ring-wedding-gold ${
                        originError ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                    {originError ? (
                      <p className="text-xs text-red-500 mt-1">
                        Please enter a valid 3-letter airport code.
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        Not sure of your code? Common ones: BOS (Boston), JFK/LGA/EWR (NYC), MIA
                        (Miami), ORD (Chicago), ATL (Atlanta), DFW (Dallas), LAX (Los Angeles).
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Depart
                    </label>
                    <input
                      type="date"
                      value={departDate}
                      onChange={(e) => setDepartDate(e.target.value)}
                      required
                      className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-wedding-gold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Return
                    </label>
                    <input
                      type="date"
                      value={returnDate}
                      onChange={(e) => setReturnDate(e.target.value)}
                      required
                      className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-wedding-gold"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Passengers
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={9}
                      value={passengers}
                      onChange={(e) => setPassengers(Math.max(1, Number(e.target.value)))}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-wedding-gold"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="mt-4 w-full inline-flex items-center justify-center bg-wedding-navy text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-base font-semibold hover:bg-opacity-90 transition-all transform hover:scale-105"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                  </svg>
                  Search on Kayak
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Opens Kayak in a new tab with your search pre-filled.
                </p>
              </form>
            </div>
          </div>

          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-lg mb-6 md:mb-8">
            <h3 className="font-serif text-2xl sm:text-3xl text-wedding-gold mb-4 md:mb-6 text-center">
              Where to Stay
            </h3>
            <div className="space-y-4 md:space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm sm:text-base text-gray-700">
                <p className="mb-2">
                  <span className="font-semibold">Please note:</span> You do not need to book the Hotel Riu Palace Bavaro, and you do not need to book through our travel agent. You are welcome to book other accommodation as the wedding is offsite.
                </p>
                <p>
                  However, we strongly encourage staying at the Riu as it is in close proximity to the wedding venue. If you book elsewhere, you will need to arrange your own transportation to the wedding venue.
                </p>
              </div>

              <div className="text-center mb-4 md:mb-6">
                <h4 className="font-serif text-xl sm:text-2xl text-wedding-navy mb-2">
                  Hotel Riu Palace Bavaro
                </h4>
                <p className="text-xs sm:text-sm text-gray-600">Our Preferred Hotel</p>
              </div>

              <div>
                <p className="text-gray-700 text-sm sm:text-base mb-4">
                  Join us at the stunning Hotel Riu Palace Bavaro, a luxurious all-inclusive
                  resort located on the pristine Arena Gorda Beach in Punta Cana. This
                  5-star hotel offers world-class amenities, beautiful oceanfront views,
                  and the perfect tropical paradise setting for our celebration.
                </p>
              </div>

              <div className="bg-wedding-cream p-4 sm:p-6 rounded-lg">
                <p className="font-semibold text-base sm:text-lg mb-3 text-center">Hotel Features</p>
                <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                  <li className="flex items-start">
                    <span className="text-wedding-gold mr-2">✓</span>
                    All-inclusive luxury resort
                  </li>
                  <li className="flex items-start">
                    <span className="text-wedding-gold mr-2">✓</span>
                    Beachfront location on Arena Gorda Beach
                  </li>
                  <li className="flex items-start">
                    <span className="text-wedding-gold mr-2">✓</span>
                    Multiple restaurants and bars
                  </li>
                  <li className="flex items-start">
                    <span className="text-wedding-gold mr-2">✓</span>
                    Spa, pools, and water sports
                  </li>
                  <li className="flex items-start">
                    <span className="text-wedding-gold mr-2">✓</span>
                    24-hour room service
                  </li>
                </ul>
              </div>

              <div className="text-center">
                <a
                  href="https://www.riu.com/en/hotel/dominican-republic/punta-cana/hotel-riu-palace-bavaro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-wedding-gold text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-base font-semibold hover:bg-opacity-90 transition-all transform hover:scale-105"
                >
                  View Hotel Details
                </a>
              </div>

              {/* Pricing Table */}
              <div className="bg-wedding-cream p-4 sm:p-6 rounded-lg">
                <p className="font-semibold text-base sm:text-lg mb-2 text-center text-wedding-navy">
                  Junior Suite - All Inclusive Rates
                </p>
                <p className="text-xs sm:text-sm text-gray-600 text-center mb-4">
                  Per person pricing
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-wedding-navy text-white">
                        <th className="px-2 sm:px-3 py-2 text-left rounded-tl-lg">Occupancy</th>
                        <th className="px-2 sm:px-3 py-2 text-right">3 nts</th>
                        <th className="px-2 sm:px-3 py-2 text-right">4 nts</th>
                        <th className="px-2 sm:px-3 py-2 text-right">5 nts</th>
                        <th className="px-2 sm:px-3 py-2 text-right rounded-tr-lg">6 nts</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      <tr className="border-b border-gray-200 bg-white">
                        <td className="px-2 sm:px-3 py-2 font-medium">Double</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$529.08</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$691.44</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$853.80</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$1,016.16</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-2 sm:px-3 py-2 font-medium">Triple</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$504.72</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$658.96</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$813.20</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$967.44</td>
                      </tr>
                      <tr className="border-b border-gray-200 bg-white">
                        <td className="px-2 sm:px-3 py-2 font-medium">Quad</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$492.54</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$642.72</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$792.90</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$943.08</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-2 sm:px-3 py-2 font-medium">5th Person</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$485.23</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$632.98</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$780.72</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$928.46</td>
                      </tr>
                      <tr className="bg-white">
                        <td className="px-2 sm:px-3 py-2 font-medium">Child (2-12)</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$285.56</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$366.75</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$447.93</td>
                        <td className="px-2 sm:px-3 py-2 text-right">$529.12</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Prices include all meals, drinks, and resort activities
                </p>
              </div>

              <div className="bg-wedding-navy bg-opacity-10 p-4 sm:p-6 rounded-lg border-l-4 border-wedding-gold">
                <p className="font-semibold text-base sm:text-lg mb-3 text-wedding-navy">Book With Our Travel Consultant</p>
                <p className="text-gray-700 text-sm sm:text-base mb-4">
                  For hotel reservations and travel arrangements, please contact our dedicated travel consultant:
                </p>
                <div className="space-y-2">
                  <p className="font-semibold text-wedding-navy text-base sm:text-lg">Ligia Landim</p>
                  <p className="text-xs sm:text-sm text-gray-600 italic">Travel Consultant</p>
                  <div className="flex items-center mt-3">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-wedding-gold mr-2 flex-shrink-0"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                    <a
                      href="mailto:info@allinclusivedestinationvibes.com"
                      className="text-wedding-navy hover:text-wedding-gold transition-colors text-sm sm:text-base"
                    >
                      info@allinclusivedestinationvibes.com
                    </a>
                  </div>
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-wedding-gold mr-2 flex-shrink-0"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                    <a
                      href="tel:+14013594894"
                      className="text-wedding-navy hover:text-wedding-gold transition-colors text-sm sm:text-base"
                    >
                      +1 (401) 359-4894
                    </a>
                  </div>
                  <div className="mt-4 pt-4 border-t border-wedding-gold border-opacity-30 space-y-3">
                    <a
                      href="https://www.vacationcrm.com/IFrameRegistration/Group?lookupId=cd2d9778-23db-4de4-a3f5-21efa7a14b84&groupId=880b8091-959e-4676-9454-8421295dc126"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-full bg-wedding-gold text-white px-6 py-3 rounded-full text-base sm:text-lg font-semibold hover:bg-opacity-90 transition-all transform hover:scale-105 shadow-md"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      Book Your Room Now
                    </a>
                    <a
                      href="https://www.vacationcrm.com/Group/ViewGroupPage?companyId=cd2d9778-23db-4de4-a3f5-21efa7a14b84&id=5aa39e79-e84b-4cd1-8efa-d6697b7b3202"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-full bg-wedding-navy text-white px-6 py-2 rounded-full text-sm sm:text-base font-medium hover:bg-opacity-90 transition-all"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      View More Information
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-wedding-cream p-3 sm:p-4 rounded-lg">
                <p className="font-semibold mb-2 text-sm sm:text-base">Recommended Dates:</p>
                <p className="text-gray-700 text-sm sm:text-base">
                  Check-in: June 23, 2027
                  <br />
                  Check-out: June 28, 2027
                  <br />
                  <span className="text-xs text-gray-500">(5 nights - Wedding is June 26)</span>
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-lg">
            <h3 className="font-serif text-2xl sm:text-3xl text-wedding-gold mb-4 md:mb-6 text-center">
              Things to Know
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <p className="font-semibold mb-2 text-sm sm:text-base">Passport Required</p>
                <p className="text-xs sm:text-sm text-gray-700">
                  US citizens need a valid passport to enter the Dominican Republic
                </p>
              </div>
              <div>
                <p className="font-semibold mb-2 text-sm sm:text-base">Weather</p>
                <p className="text-xs sm:text-sm text-gray-700">
                  June averages 85°F (29°C) with beautiful sunshine
                </p>
              </div>
              <div>
                <p className="font-semibold mb-2 text-sm sm:text-base">Currency</p>
                <p className="text-xs sm:text-sm text-gray-700">
                  Dominican Peso (DOP), but USD widely accepted
                </p>
              </div>
              <div>
                <p className="font-semibold mb-2 text-sm sm:text-base">Language</p>
                <p className="text-xs sm:text-sm text-gray-700">
                  Spanish is official, English commonly spoken at resorts
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Travel;
